import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Invoice } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from 'semantic-ui-react';
import styled from 'styled-components';
import { RateMetadataHook } from '../../../hooks/rate';
import {
  billingCycleOptions,
  BillingService,
  CommonUtility,
  ToastMessage,
} from '../../../utility';
import {
  populateRate,
  prepareRateCategories,
  RateCardFields,
  RateSearchInput,
} from '../../../page-components';
import {
  BodyContainer,
  DropdownFormField,
  HeaderBar,
  MuteText,
  NumberFormField,
  PageTitle,
  ShadowBox,
  StyledInput,
  TitleBarTitle,
  TitleHeaderBar,
} from '../../../elements';
import { CustomerDetailHook } from '../../../hooks';
import { AlertFooterBar } from '../../../elements/FooterAlert';
import { DesktopMode, MobileMode } from '../../../layouts/ResponsiveMedia';
import { TitleBackButton } from '../../../components';
import { useGlobalContext } from '../../../contexts';
import { hasRateMarginValue } from '../../../utility/billing/rate-margin';

function Header() {
  return (
    <>
      <MobileMode>
        <HeaderBar className="row tab-layout">
          <div className="col-6 pt-2">
            <PageTitle className="d-flex align-items-center">
              <Invoice size={25} />
              <span className="ml-2">Create Customer Rate</span>
            </PageTitle>
          </div>
        </HeaderBar>
      </MobileMode>
      <DesktopMode>
        <TitleHeaderBar className="row align-items-start">
          <div className="d-flex col-8 align-items-start">
            <TitleBackButton />
            <TitleBarTitle className="d-flex align-items-center mt-1">
              <Invoice size={20} className="mr-1 icon" />
              <span>Create Customer Rate</span>
            </TitleBarTitle>
          </div>
        </TitleHeaderBar>
      </DesktopMode>
    </>
  );
}

const UnitLabel = styled.div`
  border-left: none !important;
  &.error {
    border-color: #e0b4b4 !important;
    color: #e7bdbc !important;
    background: #fff6f6 !important;
  }
  font-weight: 400 !important;
  color: ${({ theme }) => theme.colors.grey} !important;
`;

const decimalMask = {
  allowDecimal: true,
  allowLeadingZeroes: false,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ',',
};

const RateSchema = yup.object().shape({
  warehouseId: yup.string().required('Warehouse is required'),
  rateMargin: yup
    .number()
    .nullable()
    .transform((_, val) => (!isNaN(val) ? Number(val) : null))
    .max(100, 'Markup cannot be more than 100%'),
  billingCycleDays: yup
    .array()
    .min(1, 'Billing Cycles is required')
    .required('Billing Cycles is required'),
});

export function CreateUserRate() {
  const [searchParams] = useSearchParams();
  const [userValue, setUserValue] = useState({});
  const [rate, setRate] = useState(null);
  const [processing, setProcessing] = useState(false);

  const {
    formState: { errors, dirtyFields },
    control,
    setValue,
    handleSubmit,
    reset,
    setError,
    watch,
  } = useForm({
    resolver: yupResolver(RateSchema),
  });

  const warehouseId = watch('warehouseId');

  useEffect(() => {
    if (CommonUtility.isValidObject(rate) && warehouseId) {
      if (rate.warehouseId?._id !== warehouseId) {
        setError('warehouseId', { message: 'Rate is from another warehouse' });
      }
    }
  }, [rate, warehouseId]);

  const navigate = useNavigate();

  const customerId = searchParams.get('customerId');
  const { data: customer, loading: detailLoading } = CustomerDetailHook(customerId);

  const { warehouseOptions, warehouseLoading } = useGlobalContext();

  const { data: metadata } = RateMetadataHook();

  useEffect(() => {
    if (CommonUtility.isValidObject(rate) && CommonUtility.isValidObject(metadata)) {
      resetFields();
    }
  }, [rate, metadata]);

  const populateUserValue = () => {
    const value = populateRate(metadata, rate);
    setUserValue(value);
    setValue('billingCycleDays', rate.billingCycleDays);
  };

  const onValueChange = (categoryName, serviceName, keyName, value) => {
    setUserValue(userValue => ({
      ...userValue,
      [categoryName]: {
        ...userValue[categoryName],
        [serviceName]: {
          ...userValue[categoryName][serviceName],
          [keyName]: value,
        },
      },
    }));
  };

  const resetFields = () => {
    populateUserValue();
    reset({
      warehouseId: '',
      billingCycleDays: [],
      rateMargin: '',
    });
  };

  const create = async formValues => {
    if (rate.warehouseId?._id !== formValues.warehouseId) {
      setError('warehouseId', { message: 'Rate is from another warehouse' });
      return;
    }

    let rateCreatedSuccessfully = false;

    try {
      setProcessing(true);
      const payload = {
        baseTemplateId: rate?._id,
        customerOrganizationId: customerId,
        warehouseId: formValues.warehouseId,
        billingCycleDays: rate?.billingCycleDays,
        categories: prepareRateCategories(userValue, metadata),
      };

      await BillingService.createCustomerTempate(payload);
      rateCreatedSuccessfully = true;

      if (hasRateMarginValue(formValues.rateMargin)) {
        const ratePayload = {
          customerOrganizationId: customerId,
          warehouseId: formValues.warehouseId,
          rateMargin: formValues.rateMargin,
        };
        await BillingService.updateMarginRate(ratePayload);
      }

      navigate(`/app/crm/user/${customerId}/rate`);
      ToastMessage.success('Customer template created successfully');
    } catch (error) {
      if (rateCreatedSuccessfully) {
        ToastMessage.warning(
          'Customer rate card created successfully but margin rate not set',
        );
      } else {
        ToastMessage.apiError(error);
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <BodyContainer className="no-header">
      <Header />
      <ShadowBox>
        <h3>Information</h3>
        <Form className="row">
          <div className="pl-0 col-4 pr-0">
            <MuteText>Base Rate</MuteText>
            <RateSearchInput inputClassName="w-100 mt-1" onResultClick={setRate} />
            {CommonUtility.isValidObject(rate) && <MuteText>{rate?.name}</MuteText>}
          </div>
          <div className="col-4 pr-0 d-flex flex-column">
            <MuteText className="mb-1">Customer</MuteText>
            <StyledInput
              disabled
              value={customer?.company_name}
              loading={detailLoading}
            />
          </div>
          {CommonUtility.isValidObject(rate) && (
            <DropdownFormField
              options={warehouseOptions}
              loading={warehouseLoading}
              control={control}
              name="warehouseId"
              error={errors.warehouseId}
              hint={errors?.warehouseId?.message}
              label="Warehouse"
              fieldClass="col-4 pr-0"
            />
          )}
          {CommonUtility.isValidObject(rate) && (
            <DropdownFormField
              control={control}
              fieldClass="col-4 px-0 mt-3"
              name="billingCycleDays"
              error={errors.billingCycleDays}
              hint={errors?.billingCycleDays?.message}
              label="Billing Cycle Days"
              options={billingCycleOptions}
              multiple
              sorted={false}
            />
          )}
          {CommonUtility.isValidObject(rate) && (
            <NumberFormField
              control={control}
              fieldClass="col-4 mt-3 pr-0"
              name="rateMargin"
              error={errors.rateMargin}
              hint={errors?.rateMargin?.message}
              label="Markup on Label Charges"
              className="align-unset fluid ui right labeled input similar-height"
              maskOptions={decimalMask}
              inputRight={
                <UnitLabel className={`ui basic label ${errors.rateMargin && 'error'}`}>
                  %
                </UnitLabel>
              }
            />
          )}
        </Form>
      </ShadowBox>
      {CommonUtility.isValidObject(rate) ? (
        <>
          <RateCardFields
            metadata={metadata}
            userValue={userValue}
            onValueChange={onValueChange}
          />
        </>
      ) : (
        <ShadowBox className="mt-3">
          <h3>Select a rate card to get started</h3>
        </ShadowBox>
      )}
      {(CommonUtility.isValidObject(rate) ||
        CommonUtility.isValidObject(dirtyFields)) && (
        <AlertFooterBar
          message="Save Changes"
          cancelButtonText="Reset"
          cancel={resetFields}
          save={handleSubmit(create)}
          loading={processing}
        />
      )}
    </BodyContainer>
  );
}
